// src/components/TenantSwitcher.jsx
//
// Seletor global de tenant — exclusivo para admin_geral.
// Fica no header (Layout) e aparece em qualquer tela.
//
// [FASE 3] Modelo de sessão via COOKIE HttpOnly — SEM token no localStorage.
// Usa o mesmo motor do modal de login (switch-tenant/clear-tenant), que:
//   • POST /api/auth/switch-tenant { cliente_id }  → reemite o cookie de sessão
//     com active_tenant_id (MESMA sessão do admin, mesmo sessionId, perfil intacto).
//   • POST /api/auth/clear-tenant                  → reemite o cookie sem
//     active_tenant_id (volta à visão global).
//   • GET  /api/auth/current-tenant                → fonte de verdade do tenant
//     ativo (lê do token, devolve { tenant } | { tenant: null }).
//
// NADA é guardado no navegador: nem token, nem adminOriginalToken. O "voltar
// à visão global" é apenas o clear-tenant. Como switch/clear preservam o
// sessionId, NENHUMA outra sessão do admin é derrubada.
//
// Lista de empresas: GET /api/auth/empresas (já exposto pelo routesLogin).

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FiChevronDown, FiSearch, FiGlobe, FiHome, FiCheck, FiLogOut } from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../services/api';
import './TenantSwitcher.css';

export default function TenantSwitcher({ user }) {
    const isAdminGeral = user?.perfil === 'admin_geral';

    const [open, setOpen]         = useState(false);
    const [empresas, setEmpresas] = useState([]);
    const [loading, setLoading]   = useState(false);
    const [busy, setBusy]         = useState(false); // durante o swap de contexto
    const [search, setSearch]     = useState('');
    const wrapRef = useRef(null);

    // [FASE 3] Tenant ativo vem do backend (current-tenant lê o token), não do
    // localStorage. { id, nome_empresa } quando dentro de um tenant; null na
    // visão global. Carregado uma vez ao montar.
    const [activeTenant, setActiveTenant] = useState(null);

    const inTenant    = !!activeTenant?.id;
    const currentName = activeTenant?.nome_empresa || '';
    const currentId   = activeTenant?.id ? String(activeTenant.id) : '';

    // Descobre o tenant ativo atual (fonte de verdade: o token, via backend).
    useEffect(() => {
        if (!isAdminGeral) return;
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get('/api/auth/current-tenant');
                if (alive) setActiveTenant(data?.tenant || null);
            } catch {
                if (alive) setActiveTenant(null);
            }
        })();
        return () => { alive = false; };
    }, [isAdminGeral]);

    // Fecha ao clicar fora
    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);

    // Carrega empresas ao abrir (uma única vez)
    const carregarEmpresas = useCallback(async () => {
        if (empresas.length > 0) return;
        setLoading(true);
        try {
            const { data } = await api.get('/api/auth/empresas');
            setEmpresas(Array.isArray(data?.empresas) ? data.empresas : []);
        } catch {
            toast.error('Não foi possível carregar a lista de empresas.');
        } finally {
            setLoading(false);
        }
    }, [empresas.length]);

    const handleToggle = () => {
        const next = !open;
        setOpen(next);
        if (next) carregarEmpresas();
    };

    // Entrar / trocar de tenant — [FASE 3] via switch-tenant (cookie).
    // O backend reemite o cookie de sessão com o active_tenant_id; não há token
    // para guardar. Recarregamos para que todo o app releia o novo contexto.
    const entrarNoTenant = useCallback(async (empresa) => {
        if (!empresa?.id || busy) return;
        // Já estou nesse tenant? só fecha.
        if (String(empresa.id) === String(currentId)) { setOpen(false); return; }

        setBusy(true);
        try {
            await api.post('/api/auth/switch-tenant', { cliente_id: empresa.id });
            // Sessão trocada no cookie. Reload para o app reidratar via /me + current-tenant.
            window.location.assign('/dashboard');
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Não foi possível acessar este tenant.');
            setBusy(false);
        }
    }, [busy, currentId]);

    // Voltar à visão global — [FASE 3] via clear-tenant (cookie).
    // Apenas zera o active_tenant_id na sessão do admin. Sem restaurar token,
    // sem adminOriginalToken. Preserva o sessionId → não derruba outras sessões.
    const voltarAoGlobal = useCallback(async () => {
        if (busy) return;
        setBusy(true);
        try {
            await api.post('/api/auth/clear-tenant');
            window.location.assign('/dashboard');
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Não foi possível voltar à visão global.');
            setBusy(false);
        }
    }, [busy]);

    const filtradas = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return empresas;
        return empresas.filter(e => (e.nome_empresa || '').toLowerCase().includes(q));
    }, [empresas, search]);

    if (!isAdminGeral) return null;

    return (
        <div className="ts-wrap" ref={wrapRef}>
            <button
                type="button"
                className={`ts-trigger ${inTenant ? 'ts-trigger--active' : ''}`}
                onClick={handleToggle}
                title={inTenant ? `Você está no tenant: ${currentName}` : 'Trocar de empresa'}
            >
                {inTenant ? <FiHome size={13} /> : <FiGlobe size={13} />}
                <span className="ts-trigger-label">
                    {inTenant ? (currentName || 'Tenant') : 'Visão global'}
                </span>
                <FiChevronDown size={13} className={`ts-chevron ${open ? 'ts-chevron--open' : ''}`} />
            </button>

            {open && (
                <div className="ts-menu" role="menu">
                    <div className="ts-menu-header">
                        <FiSearch size={13} />
                        <input
                            autoFocus
                            type="text"
                            className="ts-search"
                            placeholder="Buscar empresa..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                    {inTenant && (
                        <button
                            type="button"
                            className="ts-global-btn"
                            onClick={voltarAoGlobal}
                            disabled={busy}
                        >
                            <FiLogOut size={13} />
                            Voltar à visão global
                        </button>
                    )}

                    <div className="ts-list">
                        {loading ? (
                            <div className="ts-hint">Carregando…</div>
                        ) : filtradas.length === 0 ? (
                            <div className="ts-hint">Nenhuma empresa encontrada.</div>
                        ) : (
                            filtradas.map((e) => {
                                const ativo = String(e.id) === String(currentId);
                                return (
                                    <button
                                        key={e.id}
                                        type="button"
                                        className={`ts-item ${ativo ? 'ts-item--active' : ''}`}
                                        onClick={() => entrarNoTenant(e)}
                                        disabled={busy}
                                    >
                                        {e.logo_url
                                            ? <img src={e.logo_url} alt="" className="ts-logo" />
                                            : <span className="ts-logo ts-logo--ph">
                                                  {(e.nome_empresa || '?').charAt(0).toUpperCase()}
                                              </span>}
                                        <span className="ts-item-name">
                                            {e.nome_empresa || `Tenant #${e.id}`}
                                        </span>
                                        {ativo && <FiCheck size={13} className="ts-item-check" />}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}